import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { 
  collection, 
  query, 
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from './AuthContext';
import type { Booking } from '@/types/booking';
import { checkBookingConflicts } from '@/lib/booking-conflict';
import { generateBookingMetadata, type DuplicateOptions } from '@/lib/metadata-utils';

type BookingContextType = {
  bookings: Booking[];
  loading: boolean;
  error: Error | null;
  addBooking: (booking: Booking) => Promise<void>;
  updateBooking: (booking: Booking) => Promise<void>;
  deleteBooking: (bookingId: number) => Promise<void>;
  checkDuplicate: (booking: Booking, excludeId?: number) => any;
  getLinkedBookingsForId: (bookingId: number) => Booking[];
  breakLink: (bookingId: number) => void;
  duplicateBooking: (
    booking: Booking, 
    date: string, 
    time: string, 
    options: DuplicateOptions
  ) => Promise<void>;
};

const BookingContext = createContext<BookingContextType | undefined>(undefined);

export function BookingProvider({ children }: { children: React.ReactNode }) {
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const { toast } = useToast();
  const { user } = useAuth();

  useEffect(() => {
    if (!user) {
      setBookings([]);
      setLoading(false);
      return;
    }

    try {
      const bookingsQuery = query(collection(db, 'bookings'));
      
      // Remove includeMetadataChanges to prevent duplicate events
      const unsubscribe = onSnapshot(
        bookingsQuery,
        (snapshot) => {
          // Deduplicate bookings by ID to prevent duplicates
          const bookingsMap = new Map();
          
          snapshot.docs.forEach(doc => {
            const bookingData = {
              id: doc.id,
              ...doc.data()
            } as Booking;
            
            // Use the ID as key to ensure uniqueness
            bookingsMap.set(doc.id, bookingData);
          });
          
          // Convert map back to array
          const uniqueBookings = Array.from(bookingsMap.values());
          setBookings(uniqueBookings);
          setLoading(false);
        },
        (err) => {
          console.error('Firestore subscription error:', err);
          setError(err as Error);
          setLoading(false);
          
          toast({
            title: "Error Loading Bookings",
            description: "Failed to load bookings. Please check your connection and try again.",
            variant: "destructive"
          });
        }
      );

      return () => unsubscribe();
    } catch (err) {
      console.error('Firestore setup error:', err);
      setError(err as Error);
      setLoading(false);
    }
  }, [user, toast]);

  const cleanBookingData = useCallback((data: any) => {
    // Remove undefined values and clean up the data structure
    const cleaned = Object.entries(data).reduce((acc, [key, value]) => {
      // Skip null or undefined values
      if (value == null) return acc;
      
      // Clean up revenue object
      if (key === 'revenue') {
        const revenue = value as Booking['revenue'];
        if (!revenue) return acc;
        
        return {
          ...acc,
          revenue: {
            amount: revenue.amount || 0,
            hasNotes: Boolean(revenue.hasNotes),
            ...(revenue.hasNotes && revenue.notes ? { notes: revenue.notes } : {})
          }
        };
      }
      
      // Clean up metadata
      if (key === 'metadata') {
        const metadata = value as Booking['metadata'];
        if (!metadata) return acc;
        
        const cleanedMetadata = {
          createdAt: metadata.createdAt || new Date().toISOString(),
          lastModified: new Date().toISOString(),
          ...(metadata.originalBookingId ? { originalBookingId: metadata.originalBookingId } : {}),
          ...(metadata.duplicateVersion != null ? { duplicateVersion: metadata.duplicateVersion } : {}),
          relationships: metadata.relationships ? {
            ...(metadata.relationships.parentId ? { parentId: metadata.relationships.parentId } : {}),
            linkedBookings: Array.isArray(metadata.relationships.linkedBookings) 
              ? metadata.relationships.linkedBookings 
              : [],
            syncFields: metadata.relationships.syncFields || null
          } : null
        };

        return {
          ...acc,
          metadata: cleanedMetadata
        };
      }
      
      return { ...acc, [key]: value };
    }, {});

    return cleaned;
  }, []);

  const addBooking = useCallback(async (booking: Booking) => {
    if (!user) {
      toast({
        title: "Authentication Required",
        description: "You must be signed in to create bookings",
        variant: "destructive"
      });
      throw new Error('Must be authenticated to create bookings');
    }

    try {
      const { id, ...bookingData } = booking;
      const cleanedData = cleanBookingData(bookingData);
      
      await addDoc(collection(db, 'bookings'), {
        ...cleanedData,
        userId: user.uid,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      toast({
        title: "Booking Created",
        description: "Successfully created new booking"
      });
    } catch (err) {
      console.error('Error adding booking:', err);
      toast({
        title: "Error",
        description: "Failed to create booking",
        variant: "destructive"
      });
      throw err;
    }
  }, [user, toast, cleanBookingData]);

  const updateBooking = useCallback(async (booking: Booking) => {
    if (!user) {
      toast({
        title: "Authentication Required",
        description: "You must be signed in to update bookings",
        variant: "destructive"
      });
      throw new Error('Must be authenticated to update bookings');
    }

    try {
      const bookingRef = doc(db, 'bookings', booking.id.toString());
      const { id, ...updates } = booking;
      const cleanedData = cleanBookingData(updates);

      await updateDoc(bookingRef, {
        ...cleanedData,
        userId: user.uid, // Ensure userId is set
        updatedAt: serverTimestamp()
      });

      toast({
        title: "Booking Updated",
        description: "Successfully updated booking"
      });
    } catch (err) {
      console.error('Error updating booking:', err);
      toast({
        title: "Error",
        description: "Failed to update booking",
        variant: "destructive"
      });
      throw err;
    }
  }, [user, toast, cleanBookingData]);

  const deleteBooking = useCallback(async (bookingId: number) => {
    if (!user) {
      toast({
        title: "Authentication Required",
        description: "You must be signed in to delete bookings",
        variant: "destructive"
      });
      throw new Error('Must be authenticated to delete bookings');
    }

    try {
      const bookingRef = doc(db, 'bookings', bookingId.toString());
      await deleteDoc(bookingRef);

      toast({
        title: "Booking Deleted",
        description: "Successfully deleted booking"
      });
    } catch (err) {
      console.error('Error deleting booking:', err);
      toast({
        title: "Error",
        description: "Failed to delete booking",
        variant: "destructive"
      });
      throw err;
    }
  }, [user, toast]);

  const checkDuplicate = useCallback((booking: Booking, excludeId?: number) => {
    return checkBookingConflicts(booking, bookings, excludeId)[0] || null;
  }, [bookings]);

  const getLinkedBookingsForId = useCallback((bookingId: number) => {
    return bookings.filter(b => 
      b.metadata?.relationships?.linkedBookings?.includes(bookingId)
    );
  }, [bookings]);

  const breakLink = useCallback(async (bookingId: number) => {
    if (!user) {
      toast({
        title: "Authentication Required",
        description: "You must be signed in to break links",
        variant: "destructive"
      });
      throw new Error('Must be authenticated to break links');
    }

    try {
      const booking = bookings.find(b => b.id === bookingId);
      if (!booking?.metadata?.relationships?.linkedBookings) return;

      const bookingRef = doc(db, 'bookings', bookingId.toString());
      await updateDoc(bookingRef, {
        'metadata.relationships.linkedBookings': [],
        'metadata.relationships.syncFields': null,
        userId: user.uid, // Ensure userId is set
        updatedAt: serverTimestamp()
      });

      toast({
        title: "Links Broken",
        description: "Successfully unlinked booking"
      });
    } catch (err) {
      console.error('Error breaking links:', err);
      toast({
        title: "Error",
        description: "Failed to break links",
        variant: "destructive"
      });
      throw err;
    }
  }, [user, bookings, toast]);

  const duplicateBooking = useCallback(async (
    booking: Booking,
    date: string,
    time: string,
    options: DuplicateOptions
  ) => {
    if (!user) {
      toast({
        title: "Authentication Required",
        description: "You must be signed in to duplicate bookings",
        variant: "destructive"
      });
      throw new Error('Must be authenticated to duplicate bookings');
    }

    try {
      const metadata = generateBookingMetadata(booking, options);
      const newBooking = {
        ...booking,
        date,
        timeSlot: time,
        metadata: {
          ...metadata,
          originalBookingId: booking.id,
          duplicateVersion: (booking.metadata?.duplicateVersion || 0) + 1,
          relationships: {
            ...metadata.relationships,
            linkedBookings: options.maintainRelationship ? [booking.id] : []
          }
        }
      };

      const { id, ...bookingData } = newBooking;
      const cleanedData = cleanBookingData(bookingData);

      const docRef = await addDoc(collection(db, 'bookings'), {
        ...cleanedData,
        userId: user.uid, // Ensure userId is set
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      if (options.maintainRelationship) {
        // Update the original booking's linked bookings
        const originalRef = doc(db, 'bookings', booking.id.toString());
        await updateDoc(originalRef, {
          'metadata.relationships.linkedBookings': [
            ...(booking.metadata?.relationships?.linkedBookings || []),
            docRef.id
          ],
          'metadata.relationships.syncFields': options.syncFields,
          userId: user.uid, // Ensure userId is set
          updatedAt: serverTimestamp()
        });
      }

      toast({
        title: "Booking Duplicated",
        description: options.maintainRelationship 
          ? "Created linked duplicate booking"
          : "Created duplicate booking"
      });
    } catch (err) {
      console.error('Error duplicating booking:', err);
      toast({
        title: "Error",
        description: "Failed to duplicate booking",
        variant: "destructive"
      });
      throw err;
    }
  }, [user, cleanBookingData, toast]);

  const value = {
    bookings,
    loading,
    error,
    addBooking,
    updateBooking,
    deleteBooking,
    checkDuplicate,
    getLinkedBookingsForId,
    breakLink,
    duplicateBooking
  };

  return (
    <BookingContext.Provider value={value}>
      {children}
    </BookingContext.Provider>
  );
}

export function useBookings() {
  const context = useContext(BookingContext);
  if (!context) {
    throw new Error('useBookings must be used within a BookingProvider');
  }
  return context;
}